import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

type TabsVariant = "default" | "line";

const TabsVariantContext = React.createContext<TabsVariant>("default");

function Root(
  { variant = "default", ...props }:
    & React.ComponentProps<typeof TabsPrimitive.Root>
    & { variant?: TabsVariant; },
) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root data-slot="tabs" {...props} />
    </TabsVariantContext.Provider>
  );
}

function List({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        variant === "line"
          ? "flex w-full border-b border-border text-muted-foreground"
          : "inline-flex h-9 items-center justify-center rounded-sm bg-muted p-1 text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

// Active styling keys on two framework-set attributes (never hand-wired):
//  - `data-[active]`        — Base UI's selected-tab attribute (value-driven Tabs)
//  - `data-[status=active]` — set by a TanStack `Link` on itself when its route
//                             is active, so a router-nav Tab whose Trigger renders
//                             a `Link` self-determines active (no external value).
function Trigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Tab>) {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "focus-ring inline-flex cursor-pointer items-center justify-center text-sm font-medium whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50",
        variant === "line"
          ? "-mb-px border-b-2 border-transparent px-4 py-2 text-muted-foreground data-[active]:border-foreground data-[active]:text-foreground data-[status=active]:border-foreground data-[status=active]:text-foreground"
          : "rounded-sm px-3 py-1 data-[active]:bg-background data-[active]:text-foreground data-[active]:shadow data-[status=active]:bg-background data-[status=active]:text-foreground data-[status=active]:shadow",
        className,
      )}
      {...props}
    />
  );
}

function Content({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Panel>) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("focus-ring mt-2", className)}
      {...props}
    />
  );
}

export const Tabs = Object.assign(Root, {
  List,
  Trigger,
  Content,
});
